import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../../utils/currency';
import { formatTableDate } from '../../utils/dateFormatter';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';
const PAGE_SIZE = 500;

export function EbayOrderList({ storeId }) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate]     = useState(todayStr);

  useEffect(() => {
    if (storeId) { fetchOrders(); fetchStats(); }
  }, [storeId]);

  const fetchOrders = async (start = startDate, end = endDate) => {
    try {
      setLoading(true);
      const token  = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: 1000 });
      if (start && end) { params.append('start_date', start); params.append('end_date', end + ' 23:59:59'); }
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) { setOrders(data.orders); setCurrentPage(1); }
      else toast.error('Failed to load orders');
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const fetchStats = async (start = startDate, end = endDate) => {
    try {
      const token  = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (start && end) { params.append('start_date', start); params.append('end_date', end + ' 23:59:59'); }
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setStats(data.stats);
    } catch { console.error('Failed to load stats'); }
  };

  const handleFilter = () => { fetchOrders(startDate, endDate); fetchStats(startDate, endDate); };
  const handleClearFilter = () => { setStartDate(''); setEndDate(''); fetchOrders('', ''); fetchStats('', ''); };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Synced ${data.synced} order items!`);
        fetchOrders(); fetchStats();
      } else toast.error('Sync failed');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const getProfitMargin = () => {
    if (!stats || !stats.total_sales || stats.total_sales === 0) return '0.0';
    return ((parseFloat(stats.total_profit || 0) / parseFloat(stats.total_sales)) * 100).toFixed(1);
  };

  const filteredOrders = orders.filter(order => {
    const q = search.toLowerCase();
    return (
      order.product_title?.toLowerCase().includes(q) ||
      order.order_number?.toString().toLowerCase().includes(q) ||
      order.sku?.toLowerCase().includes(q)
    );
  });

  const totalPages     = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">eBay Orders</h1>
          <p className="text-gray-600">
            {search ? `${filteredOrders.length} of ${orders.length} order items` : `${orders.length} order items displayed`}
          </p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50">
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Sync Orders'}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Orders</p>
            <p className="text-3xl font-bold text-purple-600">{formatNumber(stats.total_orders)}</p>
            <p className="text-sm text-gray-500 mt-1">{formatNumber(stats.total_items)} items</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Revenue</p>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(stats.total_sales)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Profit</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.total_profit)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Profit Margin</p>
            <p className="text-3xl font-bold text-orange-600">{getProfitMargin()}%</p>
          </div>
        </div>
      )}

      {/* Date filter + search */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-gray-600 text-sm font-medium mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-gray-600 text-sm font-medium mb-1">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={handleFilter}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
            Apply Filter
          </button>
          <button onClick={handleClearFilter}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium">
            Show All
          </button>
          <div className="flex-1 min-w-48 relative">
            <label className="block text-gray-600 text-sm font-medium mb-1">Search</label>
            <Search className="absolute left-3 bottom-2.5 text-gray-400" size={18} />
            <input type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Product name, order #, or SKU..."
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            {search && (
              <button onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute right-3 bottom-2.5 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-blue-600 mr-2" size={24} />
              <span className="text-gray-500">Loading orders...</span>
            </div>
          ) : paginatedOrders.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Order #</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">SKU</th>
                  <th className="text-center py-4 px-6 text-gray-600 font-semibold">Qty</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Fees</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-800">{order.order_number}</td>
                    <td className="py-4 px-6 text-gray-700">{order.product_title}</td>
                    <td className="py-4 px-6">
                      {order.sku ? (
                        <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {order.sku}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center font-medium">{order.quantity}</td>
                    <td className="py-4 px-6 text-right font-semibold text-blue-600">{formatCurrency(order.selling_price)}</td>
                    <td className="py-4 px-6 text-right text-gray-700">{formatCurrency(order.cost_price)}</td>
                    <td className="py-4 px-6 text-right text-orange-600">{formatCurrency(order.ebay_fees)}</td>
                    <td className="py-4 px-6 text-right font-bold text-green-600">{formatCurrency(order.net_profit)}</td>
                    <td className="py-4 px-6 text-gray-600">{formatTableDate(order.order_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              {search ? (
                <><p className="text-gray-500 text-lg mb-2">No orders found for "{search}"</p>
                <p className="text-gray-400 text-sm">Try a different search term</p></>
              ) : (
                <><p className="text-gray-500 text-lg mb-4">No orders found</p>
                <p className="text-gray-400 text-sm">Click "Sync Orders" to import orders from eBay</p></>
              )}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx-1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                .map((p, idx) => p === '...' ? (
                  <span key={`e-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button key={p} onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${currentPage === p ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-100 text-gray-700'}`}>
                    {p}
                  </button>
                ))}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EbayOrderList;